import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ADMIN_UPDATE_EVENT,
  applyAdminUpdate,
  checkAdminUpdateNow,
  dismissAdminUpdate,
  initAdminUpdateDetection,
  isAdminUpdateAvailableFlag,
} from '../utils/adminUpdateNotifier';

const bannerStyle = {
  position: 'fixed',
  left: 12,
  right: 12,
  top: 12,
  zIndex: 10050,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '14px 16px',
  borderRadius: 12,
  background: 'rgba(12, 18, 32, 0.97)',
  border: '1px solid rgba(96, 165, 250, 0.35)',
  color: '#f1f5f9',
  boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
  maxWidth: 560,
  margin: '0 auto',
};

const primaryBtnStyle = {
  flexShrink: 0,
  border: 'none',
  borderRadius: 8,
  padding: '10px 14px',
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
  background: '#2563eb',
  color: '#fff',
};

const laterBtnStyle = {
  flexShrink: 0,
  border: 'none',
  borderRadius: 8,
  padding: '10px 12px',
  fontWeight: 500,
  fontSize: 13,
  cursor: 'pointer',
  background: 'transparent',
  color: 'rgba(241,245,249,0.75)',
};

export default function AdminUpdateBanner() {
  const location = useLocation();
  const [visible, setVisible] = useState(() => isAdminUpdateAvailableFlag());
  const [version, setVersion] = useState('pending');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!import.meta.env.PROD) return undefined;

    initAdminUpdateDetection();

    const onUpdate = (event) => {
      setVersion(event?.detail?.version || 'pending');
      setVisible(true);
      try {
        const fallback = document.getElementById('admin-update-fallback');
        if (fallback) fallback.style.display = 'none';
      } catch {
        /* ignore */
      }
    };

    window.addEventListener(ADMIN_UPDATE_EVENT, onUpdate);

    // 이벤트 레이스 대비: 플래그 폴링
    const poll = window.setInterval(() => {
      if (isAdminUpdateAvailableFlag()) setVisible(true);
    }, 4000);

    checkAdminUpdateNow(true);
    const bootTimer = window.setTimeout(() => checkAdminUpdateNow(true), 1000);

    return () => {
      window.removeEventListener(ADMIN_UPDATE_EVENT, onUpdate);
      window.clearInterval(poll);
      window.clearTimeout(bootTimer);
    };
  }, []);

  useEffect(() => {
    if (!import.meta.env.PROD) return undefined;
    const timer = window.setTimeout(() => checkAdminUpdateNow(false), 400);
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!visible) return;
    try {
      const fallback = document.getElementById('admin-update-fallback');
      if (fallback) fallback.style.display = 'none';
    } catch {
      /* ignore */
    }
  }, [visible]);

  const handleApply = useCallback(async () => {
    if (applying) return;
    setApplying(true);
    try {
      await applyAdminUpdate();
    } catch (e) {
      console.warn('[Admin update] apply failed:', e);
      setApplying(false);
    }
  }, [applying]);

  const handleLater = useCallback(() => {
    dismissAdminUpdate(version);
    setVisible(false);
  }, [version]);

  if (!visible || !import.meta.env.PROD) return null;

  return (
    <div role="status" aria-live="assertive" style={bannerStyle} data-testid="admin-update-banner">
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>새 업데이트가 있습니다</div>
        <div style={{ fontSize: 13, lineHeight: 1.45, color: 'rgba(241,245,249,0.85)' }}>
          관리자 대시보드 새 버전이 배포되었습니다. [업데이트]를 누르면 최신 화면으로 새로고침됩니다.
        </div>
      </div>
      <button type="button" style={laterBtnStyle} onClick={handleLater} disabled={applying}>
        나중에
      </button>
      <button
        type="button"
        style={{ ...primaryBtnStyle, opacity: applying ? 0.7 : 1 }}
        onClick={handleApply}
        disabled={applying}
      >
        {applying ? '업데이트 중…' : '업데이트'}
      </button>
    </div>
  );
}
